import { Plus, FileSpreadsheet, UploadCloud, BarChart3, Download, Upload, CheckCircle, Clock, Eye, Trash2, PieChart, Layers, BookOpen, PenSquare, ChevronDown, ScanLine, Filter, Users } from 'lucide-react';
import { normalizeYesToolsClassValue } from '../../utils/yesTools';
import TabPage, { TabItemProps } from '../../components/dynamic/Tab';
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from '../../constants/listToolbarButtons';
import type { PracticeExam } from '../../types/practice-exam';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import checkPermission from '../../utils/permission';
import { useAuth } from '../../contexts/AuthContext';
import { ApiService } from '../../services/api';
import { useModal } from '../../hooks/useModal';
import Modal from '../../components/ui/Modal';
import InternalExams from './InternalExams';
import MyReportCards from './MyReportCards';
import ReportCards from './ReportCards';
import OpticalUploadModal from './components/OpticalUploadModal';
import LgsOpticalUploadModal from './components/LgsOpticalUploadModal';
import ExternalSchools from './ExternalSchools';
import ExamResultsTable from './components/ExamResultsTable';
import ExamListFilter, { EMPTY_EXAM_FILTERS, ExamListFacets, ExamListFilters } from './components/ExamListFilter';
import ExamAudiencePicker, { ExamTargetDepartment } from './components/ExamAudiencePicker';
import ExamAudienceModal from './components/ExamAudienceModal';

type InternalExamLevelType = 'lgs' | 'yks';
type YksMode = 'full' | 'single_tyt' | 'single_ayt';

type AnswerKeyTestDefinition = {
  value: string;
  label: string;
  subjects: string[];
};

type AnswerKeySheet = {
  publisherName?: string;
  testName?: string;
  testCode?: string;
  columns: string[];
  rows: string[][];
};

type AnswerKeySheetTab = {
  key: string;
  label: string;
  sheet: AnswerKeySheet | null;
};

const INTERNAL_ANSWER_KEY_TEST_DEFINITIONS: Record<InternalExamLevelType, AnswerKeyTestDefinition[]> = {
  lgs: [
    { value: 'Sözel', label: 'Sözel', subjects: ['Türkçe', 'Sosyal Bilgiler T.C. İnk. Tar. ve Ata.', 'Din Kültürü ve Ahlak Bil.', 'İngilizce'] },
    { value: 'Sayısal', label: 'Sayısal', subjects: ['Matematik', 'Fen Bilimleri'] },
  ],
  yks: [
    { value: 'Türkçe', label: 'Türkçe', subjects: ['Türkçe'] },
    { value: 'Sosyal Bilimler', label: 'Sosyal Bilimler', subjects: ['Tarih', 'Coğrafya', 'Felsefe', 'Din Kültürü ve Ahlak Bilgisi', 'İlave Felsefe'] },
    { value: 'Temel Matematik', label: 'Temel Matematik', subjects: ['Temel Matematik', 'Geometri'] },
    { value: 'Fen Bilimleri', label: 'Fen Bilimleri', subjects: ['Fizik', 'Kimya', 'Biyoloji'] },
    { value: 'Türk Dili ve Edebiyatı - Sosyal Bilimler-1', label: 'Türk Dili ve Edebiyatı - Sosyal Bilimler-1', subjects: ['Türk Dili ve Edebiyatı', 'Tarih-1', 'Coğrafya-1'] },
    { value: 'Sosyal Bilimler-2', label: 'Sosyal Bilimler-2', subjects: ['Tarih-2', 'Coğrafya-2', 'Felsefe Grubu', 'Din Kültürü ve Ahlak Bilgisi', 'İlave Felsefe'] },
    { value: 'Matematik', label: 'Matematik', subjects: ['Matematik', 'Geometri'] },
    { value: 'AYT Fen Bilimleri', label: 'AYT Fen Bilimleri', subjects: ['Fizik', 'Kimya', 'Biyoloji'] },
  ],
};

export default function PracticeExamsPage() {
  const { showAlert } = useModal();
  const { user } = useAuth();

  const isInternalExam = (exam?: PracticeExam | null) => exam?.systemType === 'internal' || exam?.isInternal;
  const getExamTypeLabel = (exam?: PracticeExam | null) => isInternalExam(exam) ? 'Akademi Sistemi' : 'Yayıncı Kuruluş';
  const getExamTypeBadgeClass = (exam?: PracticeExam | null) => isInternalExam(exam)
    ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
    : 'bg-slate-200 text-slate-800';
  const getExcelColumnLabel = (index: number) => {
    let current = index + 1;
    let label = '';
    while (current > 0) {
      const remainder = (current - 1) % 26;
      label = String.fromCharCode(65 + remainder) + label;
      current = Math.floor((current - 1) / 26);
    }
    return label;
  };
  const isCenteredAnswerKeyColumn = (columnIndex: number) => {
    return columnIndex >= 0 && columnIndex <= 5;
  };
  const getInternalAnswerKeyTestDefinitions = (levelType?: PracticeExam['levelType'] | '') => {
    if (levelType === 'lgs' || levelType === 'yks') {
      return INTERNAL_ANSWER_KEY_TEST_DEFINITIONS[levelType];
    }
    return [];
  };
  const getAnswerKeySubjectsForTest = (levelType: PracticeExam['levelType'] | '' | undefined, testValue: string) => {
    const definitions = getInternalAnswerKeyTestDefinitions(levelType);
    const match = definitions.find((item) => item.value === testValue);
    return match?.subjects || [];
  };
  const getAnswerKeyUniqueOptions = (baseOptions: string[], currentValue: string) => {
    const nextOptions = [...baseOptions];
    const trimmedCurrentValue = currentValue.trim();
    if (trimmedCurrentValue && !nextOptions.includes(trimmedCurrentValue)) {
      nextOptions.unshift(trimmedCurrentValue);
    }
    return nextOptions;
  };
  const normalizeAnswerKeySheetSelections = (
    sheet: AnswerKeySheet | null,
    levelType?: PracticeExam['levelType'] | ''
  ) => {
    if (!sheet) return sheet;

    let hasChanges = false;
    const nextRows = sheet.rows.map((row) => {
      const nextRow = [...row];
      const testValue = (nextRow[1] || '').trim();
      const subjectValue = (nextRow[2] || '').trim();
      const availableSubjects = getAnswerKeySubjectsForTest(levelType, testValue);

      // Yayınevinin ders adı (Edebiyat, Matematik-2) listede olmasa da korunur: tablo açılınca siliniyor, kaydedince
      // dersler test adına düşüyordu (TED Palme AYT, 2 Kas: tek cevap düzeltmesi 54 sorunun dersini sildi). Yalnız boş hücre doldurulur.
      if (!subjectValue && availableSubjects.length === 1) {
        nextRow[2] = availableSubjects[0];
        hasChanges = true;
      }

      return nextRow;
    });

    return hasChanges ? { ...sheet, rows: nextRows } : sheet;
  };
  const normalizeAnswerKeySheetTabs = (tabs: AnswerKeySheetTab[], levelType?: PracticeExam['levelType'] | '') => {
    return tabs.map((tab) => ({
      ...tab,
      sheet: normalizeAnswerKeySheetSelections(tab.sheet, levelType),
    }));
  };
  const triggerBrowserDownload = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const userClassLevel = normalizeYesToolsClassValue(user?.selectedClassLevel);
  const isSuperAdmin = !!user?.isSuperAdmin;
  const isLGS = isSuperAdmin || ['5', '6', '7', '8'].includes(userClassLevel || '');
  const isYKS = isSuperAdmin || ['9', '10', '11', '12', 'Mezun'].includes(userClassLevel || '');

  const canViewSensitiveInfo = checkPermission('user_sensitive_info_view');
  const canUploadResults = checkPermission('practice_exam_upload_results');
  const canViewResults = checkPermission('practice_exam_view_results');
  const canCreateExam = checkPermission('practice_exam_create');
  const canListExams = checkPermission('practice_exam_list');
  const canViewInternalExams = checkPermission('practice_exam_student_list');
  const canDeleteExam = checkPermission('practice_exam_delete');
  const canManageExternalSchools = checkPermission('external_school_manage');
  const canManageExamTabs = canListExams && (canCreateExam || canUploadResults || canViewResults || canDeleteExam);
  const canViewReportCardsTab = canUploadResults || canViewResults;

  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  // Deneme listesi süzgeçleri (Görevler sayfasındaki sol panel kalıbı); telefonda üstte açılan katman
  const [examFilters, setExamFilters] = useState<ExamListFilters>(EMPTY_EXAM_FILTERS);
  const [examFacets, setExamFacets] = useState<ExamListFacets | null>(null);
  const [filterOpen, setFilterOpen] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  // Optik okuyucu sonucu yüklenecek Akademi sistemi YKS denemesi
  const [opticalExam, setOpticalExam] = useState<PracticeExam | null>(null);
  const [lgsOpticalExam, setLgsOpticalExam] = useState<PracticeExam | null>(null);
  const [showNewExamModal, setShowNewExamModal] = useState(false);
  const [showUploadResultsModal, setShowUploadResultsModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState<PracticeExam | null>(null);
  const [creating, setCreating] = useState(false);
  const [uploadingResults, setUploadingResults] = useState(false);

  const [newExamName, setNewExamName] = useState("");
  const [newExamDate, setNewExamDate] = useState("");
  const [newExamSystemType, setNewExamSystemType] = useState<'publisher' | 'internal'>('publisher');
  const [newExamLevelType, setNewExamLevelType] = useState<'lgs' | 'yks' | ''>('');
  const [newExamYksMode, setNewExamYksMode] = useState<YksMode>('full');
  // Kurum dışı (dış okul) denemeleri şimdilik LGS; dış öğrenci karnesine okulun bağlantısından ulaşır.
  const [newExamAudience, setNewExamAudience] = useState<'internal' | 'external' | 'both'>('internal');
  const [newExamGradeLevel, setNewExamGradeLevel] = useState<string>('');
  const [newExamDepartments, setNewExamDepartments] = useState<ExamTargetDepartment[]>([]);
  // Yayınevi denemesi: SAY/EA/SÖZ alan puanı + alan sırası (ör. 11. sınıf 4 × 30 soruluk alan denemesi)
  const [newExamAlanPuani, setNewExamAlanPuani] = useState(false);
  const [audienceExam, setAudienceExam] = useState<PracticeExam | null>(null);
  const [answerKeyFile, setAnswerKeyFile] = useState<File | null>(null);
  const [answerKeyTYTFile, setAnswerKeyTYTFile] = useState<File | null>(null);
  const [answerKeyAYTFile, setAnswerKeyAYTFile] = useState<File | null>(null);
  const [formDefinitionFile, setFormDefinitionFile] = useState<File | null>(null);

  const [resultsFile, setResultsFile] = useState<File | null>(null);
  // Optik form tanımı (isteğe bağlı): verilirse TXT alan alan bu tanımla okunur (sezgisel okuyucu cevapları kaydırabiliyor)
  const [resultsFormFile, setResultsFormFile] = useState<File | null>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultsExam, setResultsExam] = useState<PracticeExam | null>(null);
  const [examMetadata, setExamMetadata] = useState<{ bookletMapping?: any; testNames?: any }>({});

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStudentResult, setSelectedStudentResult] = useState<any>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [examToDelete, setExamToDelete] = useState<PracticeExam | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showAnswerKeyModal, setShowAnswerKeyModal] = useState(false);
  const [answerKeyLoading, setAnswerKeyLoading] = useState(false);
  const [answerKeyUpdating, setAnswerKeyUpdating] = useState(false);
  const [answerKeyDownloadLoading, setAnswerKeyDownloadLoading] = useState(false);
  const [answerKeyExam, setAnswerKeyExam] = useState<PracticeExam | null>(null);
  const [answerKeySheet, setAnswerKeySheet] = useState<AnswerKeySheet | null>(null);
  const [originalAnswerKeySheet, setOriginalAnswerKeySheet] = useState<AnswerKeySheet | null>(null);
  const [answerKeySheetTabs, setAnswerKeySheetTabs] = useState<AnswerKeySheetTab[]>([]);
  const [activeAnswerKeySegment, setActiveAnswerKeySegment] = useState<string | null>(null);
  const [newAnswerKeyUpload, setNewAnswerKeyUpload] = useState<File | null>(null);
  const [gainCodeEditorRowIndex, setGainCodeEditorRowIndex] = useState<number | null>(null);
  const [pendingGainCode, setPendingGainCode] = useState('');
  const [answerKeyDropdown, setAnswerKeyDropdown] = useState<{ rowIndex: number; columnIndex: 1 | 2 } | null>(null);
  const answerKeyGridRef = useRef<HTMLDivElement | null>(null);
  const answerKeyDropdownRef = useRef<HTMLDivElement | null>(null);

  const resetNewExamForm = useCallback(() => {
    setNewExamName("");
    setNewExamDate("");
    setNewExamSystemType('publisher');
    setNewExamLevelType('');
    setNewExamYksMode('full');
    setNewExamAudience('internal');
    setNewExamGradeLevel('');
    setNewExamDepartments([]);
    setNewExamAlanPuani(false);
    setAnswerKeyFile(null);
    setAnswerKeyTYTFile(null);
    setAnswerKeyAYTFile(null);
    setFormDefinitionFile(null);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(max-width: 640px)');
    const compute = () => {
      setIsMobileViewport(mql.matches);
      if (mql.matches) setFilterOpen(false);
    };
    compute();
    mql.addEventListener('change', compute);
    return () => mql.removeEventListener('change', compute);
  }, []);

  const EXAM_PAGE_SIZE = 20;
  const fetchExams = useCallback(async (page: number, searchTerm?: string) => {
    const raw: any = await ApiService.getPracticeExams({
      page,
      limit: EXAM_PAGE_SIZE,
      search: searchTerm?.trim() || undefined,
      systemType: examFilters.type === 'publisher' ? 'publisher' : undefined,
      levelType: examFilters.type === 'yks' || examFilters.type === 'lgs' ? examFilters.type : undefined,
      audience: examFilters.audience || undefined,
      gradeLevel: examFilters.gradeLevel || undefined,
      status: examFilters.status || undefined,
    });

    const rows = Array.isArray(raw?.rows) ? raw.rows : Array.isArray(raw) ? raw : [];
    const count = typeof raw?.count === 'number' ? raw.count : rows.length;
    if (raw?.facets) setExamFacets(raw.facets);

    return {
      data: rows,
      meta: {
        page,
        totalPages: Math.max(1, Math.ceil(count / EXAM_PAGE_SIZE)),
        totalItems: count,
        limit: EXAM_PAGE_SIZE,
      },
    };
  }, [examFilters]);

  const activeFilterCount = [examFilters.type, examFilters.audience, examFilters.gradeLevel, examFilters.status].filter(Boolean).length;
  const examFilterKey = `${examFilters.type}|${examFilters.audience}|${examFilters.gradeLevel || ''}|${examFilters.status}`;

  const handleCreateExam = async () => {
    const requiresFormDefinition = newExamSystemType === 'publisher';
    const requiresLevelType = newExamSystemType === 'internal';
    const requiresSplitYKSAnswerKeys = newExamSystemType === 'internal' && newExamLevelType === 'yks' && newExamYksMode === 'full';
    const requiresTYTOnlyYKSAnswerKey = newExamSystemType === 'internal' && newExamLevelType === 'yks' && newExamYksMode === 'single_tyt';
    const requiresAYTOnlyYKSAnswerKey = newExamSystemType === 'internal' && newExamLevelType === 'yks' && newExamYksMode === 'single_ayt';

    const hasRequiredAnswerKeys = requiresSplitYKSAnswerKeys
      ? !!answerKeyTYTFile && !!answerKeyAYTFile
      : requiresTYTOnlyYKSAnswerKey
        ? !!answerKeyTYTFile
        : requiresAYTOnlyYKSAnswerKey
          ? !!answerKeyAYTFile
          : !!answerKeyFile;

    if ((!hasRequiredAnswerKeys || (requiresFormDefinition && !formDefinitionFile)) || !newExamName.trim() || !newExamDate || (requiresLevelType && !newExamLevelType)) {
      await showAlert({
        title: 'Eksik Bilgi',
        message: requiresSplitYKSAnswerKeys
          ? 'Deneme adı, tarih, düzey, TYT cevap anahtarı ve AYT cevap anahtarı zorunludur.'
          : requiresTYTOnlyYKSAnswerKey
            ? 'Deneme adı, tarih, düzey ve TYT cevap anahtarı zorunludur.'
            : requiresAYTOnlyYKSAnswerKey
              ? 'Deneme adı, tarih, düzey ve AYT cevap anahtarı zorunludur.'
            : newExamSystemType === 'internal'
              ? 'Deneme adı, tarih, düzey ve cevap anahtarı zorunludur.'
              : 'Deneme adı, tarih, cevap anahtarı ve optik form tanımı zorunludur.',
        type: 'error',
      });

      return;
    };

    try {
      setCreating(true);

      const createdExam = await ApiService.createPracticeExam({
        name: newExamName.trim(),
        date: newExamDate,
        systemType: newExamSystemType,
        levelType: newExamSystemType === 'internal' && newExamLevelType ? newExamLevelType : undefined,
        yksMode: newExamSystemType === 'internal' && newExamLevelType === 'yks' ? newExamYksMode : undefined,
        audience: newExamSystemType === 'internal' && newExamLevelType === 'lgs' ? newExamAudience : undefined,
        gradeLevel: newExamSystemType === 'internal' && newExamLevelType === 'lgs' && newExamGradeLevel ? Number(newExamGradeLevel) : undefined,
        departmentIds: newExamDepartments.length ? newExamDepartments.map((d) => d.id) : undefined,
        alanPuani: newExamSystemType === 'publisher' && newExamAlanPuani ? true : undefined,
        answerKey: answerKeyFile || undefined,
        answerKeyTYT: (requiresSplitYKSAnswerKeys || requiresTYTOnlyYKSAnswerKey) ? answerKeyTYTFile || undefined : undefined,
        answerKeyAYT: (requiresSplitYKSAnswerKeys || requiresAYTOnlyYKSAnswerKey) ? answerKeyAYTFile || undefined : undefined,
        formDefinition: newExamSystemType === 'publisher' ? formDefinitionFile || undefined : undefined,
      });

      setShowNewExamModal(false);
      resetNewExamForm();
      setRefreshKey((k) => k + 1);

      await showAlert({
        title: createdExam?.hasCorrectAnswers === false ? 'Deneme Oluşturuldu' : 'Başarılı',
        message: createdExam?.hasCorrectAnswers === false
          ? 'Deneme oluşturuldu. Ancak yüklediğiniz Akademi sistemi dosyasında doğru seçenekler olmadığı için sınav değerlendirmesi henüz yapılamaz.'
          : 'Deneme başarıyla oluşturuldu.',
        type: createdExam?.hasCorrectAnswers === false ? 'warning' : 'success',
      });
    } catch (error: any) {
      await showAlert({
        title: 'Hata',
        message: error?.message || 'Deneme oluşturulurken bir hata oluştu.',
        type: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleUploadResults = async () => {
    if (!selectedExam || !resultsFile) return;

    try {
      setUploadingResults(true);
      await ApiService.uploadPracticeExamResults(selectedExam.id, resultsFile, resultsFormFile);

      setShowUploadResultsModal(false);
      setSelectedExam(null);
      setResultsFile(null);
      setResultsFormFile(null);
      setRefreshKey((k) => k + 1);

      await showAlert({
        title: 'Başarılı',
        message: 'Sonuç dosyası başarıyla yüklendi.',
        type: 'success',
      });
    } catch (error: any) {
      await showAlert({
        title: 'Hata',
        message: error?.message || 'Sonuç dosyası yüklenirken bir hata oluştu.',
        type: 'error',
      });
    } finally {
      setUploadingResults(false);
    }
  };

  const handleDeleteExam = async () => {
    if (!examToDelete) return;

    try {
      setDeleting(true);
      await ApiService.deletePracticeExam(examToDelete.id);

      setShowDeleteModal(false);
      setExamToDelete(null);
      setRefreshKey((k) => k + 1);

      await showAlert({
        title: 'Başarılı',
        message: 'Deneme başarıyla silindi.',
        type: 'success',
      });
    } catch (error: any) {
      await showAlert({
        title: 'Hata',
        message: error?.message || 'Deneme silinirken bir hata oluştu.',
        type: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const openAnswerKeyModal = async (exam: PracticeExam) => {
    try {
      setAnswerKeyLoading(true);
      setAnswerKeyExam(exam);
      setAnswerKeySheet(null);
      setOriginalAnswerKeySheet(null);
      setAnswerKeySheetTabs([]);
      setActiveAnswerKeySegment(null);
      setNewAnswerKeyUpload(null);
      setShowAnswerKeyModal(true);

      const res = await ApiService.getPracticeExamAnswerKey(exam.id);
      const nextExam = ((res as any)?.exam || exam) as PracticeExam;
      const normalizedTabs = normalizeAnswerKeySheetTabs(Array.isArray((res as any)?.sheets) ? (res as any).sheets : [], nextExam?.levelType);
      const selectedSegment = (res as any)?.activeSegment || normalizedTabs[0]?.key || null;
      const selectedSheet = normalizedTabs.find((item) => item.key === selectedSegment)?.sheet
        || normalizeAnswerKeySheetSelections((res as any)?.sheet || null, nextExam?.levelType);
      setAnswerKeySheet(selectedSheet);
      setOriginalAnswerKeySheet(selectedSheet);
      setAnswerKeySheetTabs(normalizedTabs);
      setActiveAnswerKeySegment(selectedSegment);
      setAnswerKeyExam(nextExam);
    } catch (error: any) {
      setShowAnswerKeyModal(false);
      setAnswerKeyExam(null);
      setAnswerKeySheet(null);
      setOriginalAnswerKeySheet(null);
      setAnswerKeySheetTabs([]);
      setActiveAnswerKeySegment(null);
      await showAlert({
        title: 'Hata',
        message: error?.message || 'Cevap anahtarı yüklenirken bir hata oluştu.',
        type: 'error',
      });
    } finally {
      setAnswerKeyLoading(false);
    }
  };

  const handleSwitchAnswerKeySegment = async (segment: string) => {
    if (!answerKeyExam || segment === activeAnswerKeySegment) return;

    try {
      setAnswerKeyLoading(true);
      setNewAnswerKeyUpload(null);
      const res = await ApiService.getPracticeExamAnswerKey(answerKeyExam.id, segment);
      const nextExam = ((res as any)?.exam || answerKeyExam) as PracticeExam;
      const normalizedTabs = normalizeAnswerKeySheetTabs(Array.isArray((res as any)?.sheets) ? (res as any).sheets : [], nextExam?.levelType);
      const selectedSegment = (res as any)?.activeSegment || segment;
      const selectedSheet = normalizedTabs.find((item) => item.key === selectedSegment)?.sheet
        || normalizeAnswerKeySheetSelections((res as any)?.sheet || null, nextExam?.levelType);

      setAnswerKeyExam(nextExam);
      setAnswerKeySheetTabs(normalizedTabs);
      setActiveAnswerKeySegment(selectedSegment);
      setAnswerKeySheet(selectedSheet);
      setOriginalAnswerKeySheet(selectedSheet);
      setAnswerKeyDropdown(null);
      handleCloseGainCodeEditor();
    } catch (error: any) {
      await showAlert({
        title: 'Hata',
        message: error?.message || 'Cevap anahtarı sekmesi yüklenirken bir hata oluştu.',
        type: 'error',
      });
    } finally {
      setAnswerKeyLoading(false);
    }
  };

  const handleDownloadAnswerKey = async () => {
    if (!answerKeyExam) return;

    try {
      setAnswerKeyDownloadLoading(true);
      const { blob, fileName } = await ApiService.exportPracticeExamAnswerKey(answerKeyExam.id, activeAnswerKeySegment || undefined);
      triggerBrowserDownload(blob, fileName || `${answerKeyExam.name || 'deneme'}-cevap-anahtari.xlsx`);
    } catch (error: any) {
      await showAlert({
        title: 'Hata',
        message: error?.message || 'Cevap anahtarı indirilirken bir hata oluştu.',
        type: 'error',
      });
    } finally {
      setAnswerKeyDownloadLoading(false);
    }
  };

  const handleUpdateAnswerKey = async () => {
    if (!answerKeyExam || !newAnswerKeyUpload) return;

    try {
      setAnswerKeyUpdating(true);
      const updatedExam = await ApiService.updatePracticeExamAnswerKey(answerKeyExam.id, newAnswerKeyUpload, activeAnswerKeySegment || undefined);
      const refreshed = await ApiService.getPracticeExamAnswerKey(answerKeyExam.id, activeAnswerKeySegment || undefined);
      const nextExam = ((refreshed as any)?.exam || updatedExam) as PracticeExam;
      const normalizedTabs = normalizeAnswerKeySheetTabs(Array.isArray((refreshed as any)?.sheets) ? (refreshed as any).sheets : [], nextExam?.levelType);
      const selectedSegment = (refreshed as any)?.activeSegment || activeAnswerKeySegment || normalizedTabs[0]?.key || null;
      const normalizedSheet = normalizedTabs.find((item) => item.key === selectedSegment)?.sheet
        || normalizeAnswerKeySheetSelections((refreshed as any)?.sheet || null, nextExam?.levelType);

      setAnswerKeySheet(normalizedSheet);
      setOriginalAnswerKeySheet(normalizedSheet);
      setAnswerKeySheetTabs(normalizedTabs);
      setActiveAnswerKeySegment(selectedSegment);
      setAnswerKeyExam(nextExam);
      setNewAnswerKeyUpload(null);
      setRefreshKey((k) => k + 1);

      await showAlert({
        title: 'Başarılı',
        message: updatedExam?.regeneratedResultsCount
          ? `Cevap anahtarı güncellendi. ${updatedExam.regeneratedResultsCount} sonuç yeni anahtarla yeniden puanlandı; öğrencilerin işaretleri korundu.`
          : 'Cevap anahtarı güncellendi.',
        type: 'success',
      });
    } catch (error: any) {
      await showAlert({
        title: 'Hata',
        message: error?.message || 'Cevap anahtarı güncellenirken bir hata oluştu.',
        type: 'error',
      });
    } finally {
      setAnswerKeyUpdating(false);
    }
  };

  const handleAnswerKeyMetaChange = (field: 'publisherName' | 'testName' | 'testCode', value: string) => {
    setAnswerKeySheet((current) => current ? { ...current, [field]: value } : current);
  };

  const handleAnswerKeyHeaderChange = (columnIndex: number, value: string) => {
    setAnswerKeySheet((current) => {
      if (!current) return current;
      const nextColumns = [...current.columns];
      nextColumns[columnIndex] = value;
      return { ...current, columns: nextColumns };
    });
  };

  const handleAnswerKeyCellChange = (rowIndex: number, columnIndex: number, value: string) => {
    setAnswerKeySheet((current) => {
      if (!current) return current;
      const nextRows = current.rows.map((row) => [...row]);
      if (!nextRows[rowIndex]) {
        nextRows[rowIndex] = Array.from({ length: current.columns.length }, () => '');
      }
      nextRows[rowIndex][columnIndex] = value;
      return { ...current, rows: nextRows };
    });
  };

  const handleAnswerKeyTestChange = (rowIndex: number, value: string) => {
    setAnswerKeySheet((current) => {
      if (!current) return current;
      const nextRows = current.rows.map((row) => [...row]);
      if (!nextRows[rowIndex]) {
        nextRows[rowIndex] = Array.from({ length: current.columns.length }, () => '');
      }

      nextRows[rowIndex][1] = value;

      const availableSubjects = getAnswerKeySubjectsForTest(answerKeyExam?.levelType, value);
      const currentSubject = (nextRows[rowIndex][2] || '').trim();
      if (availableSubjects.length === 1) {
        nextRows[rowIndex][2] = availableSubjects[0];
      } else if (availableSubjects.length > 0 && currentSubject && !availableSubjects.includes(currentSubject)) {
        nextRows[rowIndex][2] = '';
      }

      return { ...current, rows: nextRows };
    });
  };

  const handleToggleAnswerKeyDropdown = (rowIndex: number, columnIndex: 1 | 2) => {
    setAnswerKeyDropdown((current) => (
      current?.rowIndex === rowIndex && current?.columnIndex === columnIndex
        ? null
        : { rowIndex, columnIndex }
    ));
  };

  const handleSelectAnswerKeyDropdownOption = (rowIndex: number, columnIndex: 1 | 2, value: string) => {
    if (columnIndex === 1) {
      handleAnswerKeyTestChange(rowIndex, value);
    } else {
      handleAnswerKeyCellChange(rowIndex, columnIndex, value);
    }
    setAnswerKeyDropdown(null);
  };

  const handleAddGainCode = (rowIndex: number) => {
    setGainCodeEditorRowIndex(rowIndex);
    setPendingGainCode('');
  };

  const handleConfirmAddGainCode = () => {
    if (gainCodeEditorRowIndex === null) return;

    const nextCode = pendingGainCode.trim();
    if (!nextCode) return;

    setAnswerKeySheet((current) => {
      if (!current) return current;
      const nextRows = current.rows.map((row) => [...row]);
      if (!nextRows[gainCodeEditorRowIndex]) return current;

      const currentCodes = (nextRows[gainCodeEditorRowIndex][6] || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      if (!currentCodes.includes(nextCode)) {
        currentCodes.push(nextCode);
      }

      nextRows[gainCodeEditorRowIndex][6] = currentCodes.join(', ');
      return { ...current, rows: nextRows };
    });

    setGainCodeEditorRowIndex(null);
    setPendingGainCode('');
  };

  const handleCloseGainCodeEditor = () => {
    setGainCodeEditorRowIndex(null);
    setPendingGainCode('');
  };

  const handleRemoveGainCode = (rowIndex: number, gainCode: string) => {
    setAnswerKeySheet((current) => {
      if (!current) return current;
      const nextRows = current.rows.map((row) => [...row]);
      if (!nextRows[rowIndex]) return current;

      const currentCodes = (nextRows[rowIndex][6] || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .filter((item) => item !== gainCode);

      nextRows[rowIndex][6] = currentCodes.join(', ');
      return { ...current, rows: nextRows };
    });
  };

  const handleAddAnswerKeyRow = () => {
    setAnswerKeySheet((current) => {
      if (!current) return current;
      return {
        ...current,
        rows: [...current.rows, Array.from({ length: current.columns.length }, () => '')],
      };
    });
  };

  const handleRemoveAnswerKeyRow = (rowIndex: number) => {
    setAnswerKeySheet((current) => {
      if (!current) return current;
      return {
        ...current,
        rows: current.rows.filter((_, index) => index !== rowIndex),
      };
    });
  };

  const handleSaveAnswerKeySheet = async () => {
    if (!answerKeyExam || !answerKeySheet) return;

    try {
      setAnswerKeyUpdating(true);
      const updatedExam = await ApiService.updatePracticeExamAnswerKeySheet(answerKeyExam.id, answerKeySheet, activeAnswerKeySegment || undefined);
      const refreshed = await ApiService.getPracticeExamAnswerKey(answerKeyExam.id, activeAnswerKeySegment || undefined);
      const nextExam = ((refreshed as any)?.exam || updatedExam) as PracticeExam;
      const normalizedTabs = normalizeAnswerKeySheetTabs(Array.isArray((refreshed as any)?.sheets) ? (refreshed as any).sheets : [], nextExam?.levelType);
      const selectedSegment = (refreshed as any)?.activeSegment || activeAnswerKeySegment || normalizedTabs[0]?.key || null;
      const normalizedSheet = normalizedTabs.find((item) => item.key === selectedSegment)?.sheet
        || normalizeAnswerKeySheetSelections((refreshed as any)?.sheet || null, nextExam?.levelType);

      setAnswerKeySheet(normalizedSheet);
      setOriginalAnswerKeySheet(normalizedSheet);
      setAnswerKeySheetTabs(normalizedTabs);
      setActiveAnswerKeySegment(selectedSegment);
      setAnswerKeyExam(nextExam);
      setRefreshKey((k) => k + 1);

      await showAlert({
        title: 'Başarılı',
        message: updatedExam?.regeneratedResultsCount
          ? `Cevap anahtarı kaydedildi. ${updatedExam.regeneratedResultsCount} sonuç yeni anahtarla yeniden puanlandı; öğrencilerin işaretleri korundu.`
          : 'Cevap anahtarı kaydedildi.',
        type: 'success',
      });
    } catch (error: any) {
      await showAlert({
        title: 'Hata',
        message: error?.message || 'Cevap anahtarı kaydedilirken bir hata oluştu.',
        type: 'error',
      });
    } finally {
      setAnswerKeyUpdating(false);
    }
  };

  useEffect(() => {
    if (!showAnswerKeyModal || !answerKeyGridRef.current) return;

    answerKeyGridRef.current.scrollLeft = 0;
    answerKeyGridRef.current.scrollTop = 0;
  }, [showAnswerKeyModal]);

  useEffect(() => {
    if (!answerKeyDropdown) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (answerKeyDropdownRef.current && !answerKeyDropdownRef.current.contains(event.target as Node)) {
        setAnswerKeyDropdown(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [answerKeyDropdown]);

  useEffect(() => {
    if (!showAnswerKeyModal) {
      setAnswerKeyDropdown(null);
    }
  }, [showAnswerKeyModal]);

  const answerKeyColumnWidths = useMemo(() => {
    if (!answerKeySheet) return [];

    return answerKeySheet.columns.map((column, columnIndex) => {
      const values: string[] = [column || ''];
      const headerLength = (column || '').length;

      if (columnIndex === 0) {
        values.push(answerKeySheet.publisherName || '', answerKeySheet.testCode || '');
      } else if (columnIndex === 1) {
        values.push(answerKeySheet.testName || '');
      }

      answerKeySheet.rows.forEach((row) => {
        const value = row?.[columnIndex];
        if (value !== undefined && value !== null) {
          values.push(String(value));
        }
      });

      const longestLength = values.reduce((max, value) => Math.max(max, value.length), 0);
      const isCompactColumn = columnIndex >= 0 && columnIndex <= 6;
      const pixelsPerChar = isCompactColumn ? 6.2 : 7.1;
      const horizontalPadding = isCompactColumn ? 24 : 30;
      const minWidth = isCompactColumn ? 72 : 96;
      const maxWidth = isCompactColumn ? 260 : 520;
      const estimatedFromContent = Math.round((longestLength * pixelsPerChar) + horizontalPadding);
      const estimatedFromHeader = Math.round((headerLength * 7.4) + 34);

      let estimatedWidth = Math.max(
        minWidth,
        Math.min(maxWidth, Math.max(estimatedFromContent, estimatedFromHeader))
      );

      if (columnIndex === 6) {
        const longestGainCodeLength = answerKeySheet.rows.reduce((max, row) => {
          const codes = String(row?.[6] || '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
          const rowLongest = codes.reduce((rowMax, code) => Math.max(rowMax, code.length), 0);
          return Math.max(max, rowLongest);
        }, 0);

        const gainBadgeWidth = Math.round((longestGainCodeLength * 6.8) + 28);
        const plusButtonWidth = 28;
        const badgeGap = 8;
        estimatedWidth = Math.max(
          estimatedWidth,
          Math.min(maxWidth, gainBadgeWidth + plusButtonWidth + badgeGap + 18)
        );
      }

      return estimatedWidth;
    });
  }, [answerKeySheet]);

  const answerKeyTestDefinitions = useMemo(
    () => getInternalAnswerKeyTestDefinitions(answerKeyExam?.levelType),
    [answerKeyExam?.levelType]
  );

  const hasAnswerKeySheetChanges = useMemo(() => {
    if (!answerKeySheet || !originalAnswerKeySheet) return false;
    return JSON.stringify(answerKeySheet) !== JSON.stringify(originalAnswerKeySheet);
  }, [answerKeySheet, originalAnswerKeySheet]);

  // Kurum içi denemede "Sonuçlar" Karne bölümünü o denemeyle açar (karne, yazdırma, analiz orada);
  // kurum dışı denemede pencere kalır: okul süzgeci, kimliği eksikler ve kimlik düzeltme orada, binlerce satır sayfalı gelir.
  const openExamResults = useCallback((exam: PracticeExam) => {
    if ((exam.audience || 'internal') === 'internal' && canViewReportCardsTab) {
      navigate(`?tab=reports&exam=${encodeURIComponent(exam.id)}`);
      return;
    }
    setResultsExam(exam);
    setShowResultsModal(true);
  }, [navigate, canViewReportCardsTab]);

  const renderExamItem = useCallback(
    (exam: PracticeExam, viewMode: string = 'list') => {
      const dateLabel = exam.date ? new Date(exam.date).toLocaleDateString('tr-TR') : 'Tarih belirtilmemiş';
      const internalExam = isInternalExam(exam);
      const hasResults = internalExam || (exam as any)?.stats !== false;
      const canOpenResults = internalExam || (exam as any)?.stats !== false;
      const examTypeLabel = getExamTypeLabel(exam);

      if (viewMode === 'grid') {
        return (
          <div
            key={exam.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow flex flex-col h-full">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-sky-700" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate" title={exam.name}>
                  {exam.name}
                </h3>
                <p className="text-xs text-gray-500 mt-1">{dateLabel}</p>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex flex-wrap items-center gap-2">
                {hasResults ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm max-w-full min-w-0">
                    <CheckCircle className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">YÜKLENDİ</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-sm max-w-full min-w-0">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">BEKLENİYOR</span>
                  </span>
                )}
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getExamTypeBadgeClass(exam)}`}>
                  {examTypeLabel}
                </span>
                {exam.audience && exam.audience !== 'internal' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm bg-amber-50 text-amber-800 border border-amber-200">
                    {exam.audience === 'external' ? 'Kurum dışı' : 'Kurum içi + dışı'}{exam.gradeLevel ? ` · ${exam.gradeLevel}. sınıf` : ''}
                  </span>
                )}
                {!!exam.targetDepartments?.length && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold shadow-sm bg-sky-50 text-sky-800 border border-sky-200 max-w-full min-w-0" title="Yalnız bu bölümler görür">
                    <Users className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{exam.targetDepartments.map((d) => d.name).join(', ')}</span>
                  </span>
                )}
              </div>
            </div>

            <div className="mt-auto flex flex-col gap-2">
              {canUploadResults && !internalExam && (
                <button
                  onClick={() => {
                    setSelectedExam(exam);
                    setShowUploadResultsModal(true);
                  }}
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 border border-blue-600 transition-colors">
                  <UploadCloud size={16} />
                  Sonuç Yükle
                </button>
              )}
              {canUploadResults && internalExam && exam.levelType === 'yks' && (
                <button
                  onClick={() => setOpticalExam(exam)}
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 border border-blue-600 transition-colors">
                  <ScanLine size={16} />
                  Optik Yükle
                </button>
              )}
              {canUploadResults && internalExam && exam.levelType === 'lgs' && (
                <button
                  onClick={() => setLgsOpticalExam(exam)}
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 border border-blue-600 transition-colors">
                  <ScanLine size={16} />
                  Optik Yükle
                </button>
              )}
              {canCreateExam && internalExam && (
                <button
                  onClick={() => openAnswerKeyModal(exam)}
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors">
                  <PenSquare size={16} />
                  Cevap Anahtarı
                </button>
              )}
              {canCreateExam && (exam.audience || 'internal') !== 'external' && (
                <button
                  onClick={() => setAudienceExam(exam)}
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 transition-colors">
                  <Users size={16} />
                  Ayarlar
                </button>
              )}
              {canViewResults && (
                <button
                  onClick={() => {
                    if (!canOpenResults) return;
                    openExamResults(exam);
                  }}
                  disabled={!canOpenResults}
                  title={!canOpenResults ? "Sonuç yükleyiniz." : undefined}
                  className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium border transition-colors
                    ${!canOpenResults
                      ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}>
                  <BarChart3 size={16} />
                  Sonuçlar
                </button>
              )}

              {canDeleteExam && (
                <button
                  onClick={() => {
                    setExamToDelete(exam);
                    setShowDeleteModal(true);
                  }}
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors">
                  <Trash2 size={16} />
                  Sil
                </button>
              )}
            </div>
          </div>
        );
      }

      return (
        <div key={exam.id} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow mb-2">
          {/* Solda ad + tarih + rozetler, sağda düğmeler; sığmazsa (dar ekran, süzgeç paneli açık) düğmeler kendiliğinden alta sarılır */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-[1_1_20rem] items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-sky-700" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 line-clamp-2 break-words" title={exam.name}>
                  {exam.name}
                </h3>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-gray-500">{dateLabel}</span>
                  {hasResults ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm">
                      <CheckCircle className="w-3 h-3 flex-shrink-0" />
                      YÜKLENDİ
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-sm">
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      BEKLENİYOR
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold shadow-sm ${getExamTypeBadgeClass(exam)}`}>
                    {examTypeLabel}
                  </span>
                  {exam.audience && exam.audience !== 'internal' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold shadow-sm bg-amber-50 text-amber-800 border border-amber-200 whitespace-nowrap">
                      {exam.audience === 'external' ? 'Kurum dışı' : 'Kurum içi + dışı'}{exam.gradeLevel ? ` · ${exam.gradeLevel}. sınıf` : ''}
                    </span>
                  )}
                  {!!exam.targetDepartments?.length && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold shadow-sm bg-sky-50 text-sky-800 border border-sky-200 max-w-full min-w-0" title="Yalnız bu bölümler görür">
                      <Users className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{exam.targetDepartments.map((d) => d.name).join(', ')}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              {canUploadResults && !internalExam && (
                <button
                  onClick={() => {
                    setSelectedExam(exam);
                    setShowUploadResultsModal(true);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 border border-blue-600 transition-colors whitespace-nowrap">
                  <UploadCloud size={16} />
                  Sonuç Yükle
                </button>
              )}
              {canUploadResults && internalExam && exam.levelType === 'yks' && (
                <button
                  onClick={() => setOpticalExam(exam)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 border border-blue-600 transition-colors whitespace-nowrap">
                  <ScanLine size={16} />
                  Optik Yükle
                </button>
              )}
              {canUploadResults && internalExam && exam.levelType === 'lgs' && (
                <button
                  onClick={() => setLgsOpticalExam(exam)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 border border-blue-600 transition-colors whitespace-nowrap">
                  <ScanLine size={16} />
                  Optik Yükle
                </button>
              )}
              {canCreateExam && internalExam && (
                <button
                  onClick={() => openAnswerKeyModal(exam)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors whitespace-nowrap">
                  <PenSquare size={16} />
                  Cevap Anahtarı
                </button>
              )}
              {canCreateExam && (exam.audience || 'internal') !== 'external' && (
                <button
                  onClick={() => setAudienceExam(exam)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 transition-colors whitespace-nowrap">
                  <Users size={16} />
                  Ayarlar
                </button>
              )}
              {canViewResults && (
                <button
                  onClick={() => {
                    if (!canOpenResults) return;
                    openExamResults(exam);
                  }}
                  disabled={!canOpenResults}
                  title={!canOpenResults ? "Sonuç yükleyiniz." : undefined}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border transition-colors whitespace-nowrap
                    ${!canOpenResults
                      ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}>
                  <BarChart3 size={16} />
                  Sonuçlar
                </button>
              )}

              {canDeleteExam && (
                <button
                  onClick={() => {
                    setExamToDelete(exam);
                    setShowDeleteModal(true);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors whitespace-nowrap">
                  <Trash2 size={16} />
                  Sil
                </button>
              )}
            </div>
          </div>
        </div>
      );
    },
    [canCreateExam, canUploadResults, canViewResults, getExamTypeBadgeClass, getExamTypeLabel, isInternalExam, openAnswerKeyModal, showAlert, openExamResults]
  );

  const examsActions = useMemo(() => (
    <div className={LIST_TOOLBAR_ROW}>
      <button
        type="button"
        onClick={() => setFilterOpen((prev) => !prev)}
        className={`${LIST_TOOLBAR_BTN} ${filterOpen
          ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600'
          : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
          }`}
        title="Filtrele"
        aria-pressed={filterOpen}>
        <Filter className={LIST_TOOLBAR_ICON} />
        <span>Filtrele{activeFilterCount ? ` (${activeFilterCount})` : ''}</span>
      </button>
      {canCreateExam && (
        <button
          type="button"
          onClick={() => setShowNewExamModal(true)}
          className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600`}>
          <Plus className={LIST_TOOLBAR_ICON} />
          <span>Yeni Deneme</span>
        </button>
      )}
    </div>
  ),
    [canCreateExam, filterOpen, activeFilterCount]
  );

  const examsTab: TabItemProps = useMemo(
    () => ({
      type: 'list',
      title: 'Tüm Denemeler',
      path: 'practice-exams',
      tabId: 'practice-exams',
      actions: examsActions,
      isSearch: true,
      showToggle: true,
      leftArea: !isMobileViewport && filterOpen
        ? () => <ExamListFilter filters={examFilters} facets={examFacets} onChange={setExamFilters} />
        : undefined,
      refreshKey: `practice-exams-${refreshKey}-${examFilterKey}`,
      filterKey: examFilterKey,
      emptyMessage: activeFilterCount ? 'Süzgece uyan deneme yok.' : 'Henüz deneme yok.',
      item: renderExamItem,
      fetcher: fetchExams,
    }),
    [examsActions, renderExamItem, refreshKey, fetchExams, isMobileViewport, filterOpen, examFilters, examFacets, examFilterKey, activeFilterCount]
  );

  const reportCardsTab: TabItemProps = useMemo(
    () => ({
      type: 'custom',
      title: 'Karne',
      path: 'reports',
      tabId: 'reports',
      customComponent: <ReportCards />
    }),
    []
  );

  const myReportsTab: TabItemProps = useMemo(
    () => ({
      type: 'custom',
      title: 'Karnem',
      path: 'my-reports',
      tabId: 'my-reports',
      customComponent: <MyReportCards />
    }),
    []
  );

  const externalSchoolsTab: TabItemProps = useMemo(
    () => ({
      type: 'custom',
      title: 'Dış Kurumlar',
      path: 'external-schools',
      tabId: 'external-schools',
      customComponent: <ExternalSchools />
    }),
    []
  );

  const internalExamsTab: TabItemProps = useMemo(
    () => ({
      type: 'custom',
      title: 'Denemeler',
      path: 'internal-exams',
      tabId: 'internal-exams',
      customComponent: <InternalExams />
    }),
    []
  );

  const tabs = useMemo(() => {
    const t = [];

    if (canManageExamTabs) {
      t.push(examsTab);
    }

    if (canViewReportCardsTab) {
      t.push(reportCardsTab);
    }

    if (canViewInternalExams) {
      t.push(internalExamsTab);
    }

    if (canManageExternalSchools) {
      t.push(externalSchoolsTab);
    }

    t.push(myReportsTab);

    return t;
  }, [examsTab, reportCardsTab, myReportsTab, internalExamsTab, externalSchoolsTab, canManageExamTabs, canViewReportCardsTab, canViewInternalExams, canManageExternalSchools]);

  const isCreatingInternalYKS = newExamSystemType === 'internal' && newExamLevelType === 'yks';
  const answerKeyLabel = newExamSystemType === 'internal' ? 'Cevap Anahtarı (XLSX)' : 'Cevap Anahtarı (XLSX)';
  const answerKeyHelpText = isCreatingInternalYKS
    ? newExamYksMode === 'single_tyt'
      ? 'Sadece TYT denemesi için yalnızca TYT cevap anahtarını yükleyin.'
      : newExamYksMode === 'single_ayt'
        ? 'Sadece AYT denemesi (ör. 11. sınıf alan denemesi) için yalnızca AYT cevap anahtarını yükleyin. Öğrenci kendi alanının (SAY / EA / SÖZ) puanıyla, alan içinde sıralanır.'
        : 'YKS için TYT ve AYT cevap anahtarlarını ayrı ayrı yükleyin.'
    : newExamSystemType === 'internal'
      ? 'Akademi sistemi cevap anahtarı dosyasını yükleyin.'
      : 'Deneme cevap anahtarı dosyasını yükleyin.';
  const canShowInternalSingleSample = newExamSystemType !== 'internal' || newExamLevelType === 'lgs';
  const answerKeySampleHref = newExamSystemType === 'internal'
    ? newExamLevelType === 'lgs'
      ? '/lgs-ornek.xlsx'
      : ''
    : '/cevap.xlsx';
  const answerKeySampleText = newExamSystemType === 'internal'
    ? newExamLevelType === 'lgs'
      ? 'LGS Örnek İndir'
      : ''
    : 'Örnek İndir';

  return (
    <>
      <TabPage title="Denemeler" tabs={tabs} />

      {/* Telefonda süzgeç paneli üstte açılır (Görevler sayfasıyla aynı) */}
      {isMobileViewport && filterOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setFilterOpen(false)}
            aria-label="Filtre panelini kapat"
          />
          <div className="fixed left-3 right-3 top-[188px] z-50 max-h-[65vh] overflow-y-auto rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
            <ExamListFilter filters={examFilters} facets={examFacets} onChange={setExamFilters} fullWidth onClose={() => setFilterOpen(false)} />
          </div>
        </>
      )}

      {audienceExam && (
        <ExamAudienceModal
          exam={audienceExam}
          onClose={() => setAudienceExam(null)}
          onSaved={() => {
            setAudienceExam(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      <Modal
        isOpen={showNewExamModal}
        onClose={() => {
          setShowNewExamModal(false);
          resetNewExamForm();
        }}
        title="Yeni Deneme Oluştur"
        size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deneme Adı <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={newExamName}
              onChange={(e) => setNewExamName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="Örn. TYT 1. Deneme"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deneme Tarihi <span className="text-red-500">*</span></label>
            <input
              type="date"
              value={newExamDate}
              onChange={(e) => setNewExamDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deneme Türü <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  setNewExamSystemType('publisher');
                  setNewExamLevelType('');
                  setAnswerKeyTYTFile(null);
                  setAnswerKeyAYTFile(null);
                }}
                className={`px-3 py-3 rounded-lg border text-sm font-medium transition-colors ${newExamSystemType === 'publisher'
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
              >
                Yayıncı Kuruluş Optiği
              </button>
              <button
                type="button"
                onClick={() => {
                  setNewExamSystemType('internal');
                  setFormDefinitionFile(null);
                }}
                className={`px-3 py-3 rounded-lg border text-sm font-medium transition-colors ${newExamSystemType === 'internal'
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
              >
                Akademi Sistemi Optiği
              </button>
            </div>
          </div>

          {newExamSystemType === 'internal' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Düzey Türü <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setNewExamLevelType('lgs');
                    setNewExamYksMode('full');
                    setAnswerKeyTYTFile(null);
                    setAnswerKeyAYTFile(null);
                  }}
                  className={`px-3 py-3 rounded-lg border text-sm font-medium transition-colors ${newExamLevelType === 'lgs'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  LGS (Ortaokul Düzeyi)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNewExamLevelType('yks');
                    setNewExamYksMode('full');
                    setAnswerKeyFile(null);
                  }}
                  className={`px-3 py-3 rounded-lg border text-sm font-medium transition-colors ${newExamLevelType === 'yks'
                    ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  YKS (Lise ve Mezun Düzeyi)
                </button>
              </div>
            </div>
          )}

          {newExamSystemType === 'internal' && newExamLevelType === 'lgs' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kimler için?</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {([
                    ['internal', 'Kurum içi', 'Bizim öğrencilerimiz'],
                    ['external', 'Kurum dışı', 'Dış okullar (hesapsız)'],
                    ['both', 'İkisi', 'Hem bizim hem dış okullar'],
                  ] as const).map(([value, label, hint]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setNewExamAudience(value)}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors text-left ${newExamAudience === value
                        ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <span className="block">{label}</span>
                      <span className="block text-xs font-normal text-gray-500">{hint}</span>
                    </button>
                  ))}
                </div>
                {newExamAudience !== 'internal' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Kurum dışı deneme bizim öğrencilerin "Denemeler" listesinde görünmez ve çevrim içi çözülemez. Dış öğrenciler karnelerine okullarının bağlantısından T.C. kimlik + okul numarasıyla ulaşır.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sınıf</label>
                <select
                  value={newExamGradeLevel}
                  onChange={(e) => setNewExamGradeLevel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">Belirtilmedi</option>
                  {[5, 6, 7, 8].map((grade) => <option key={grade} value={grade}>{grade}. sınıf</option>)}
                </select>
              </div>
            </>
          )}

          {!(newExamSystemType === 'internal' && newExamLevelType === 'lgs' && newExamAudience === 'external') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kimler görsün</label>
              <ExamAudiencePicker value={newExamDepartments} onChange={setNewExamDepartments} disabled={creating} />
            </div>
          )}

          {isCreatingInternalYKS && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">YKS Uygulama Türü <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setNewExamYksMode('full');
                    setAnswerKeyFile(null);
                  }}
                  className={`px-3 py-3 rounded-lg border text-sm font-medium transition-colors ${newExamYksMode === 'full'
                    ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  TYT + AYT
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNewExamYksMode('single_tyt');
                    setAnswerKeyAYTFile(null);
                    setAnswerKeyFile(null);
                  }}
                  className={`px-3 py-3 rounded-lg border text-sm font-medium transition-colors ${newExamYksMode === 'single_tyt'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Sadece TYT
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNewExamYksMode('single_ayt');
                    setAnswerKeyTYTFile(null);
                    setAnswerKeyFile(null);
                  }}
                  className={`px-3 py-3 rounded-lg border text-sm font-medium transition-colors ${newExamYksMode === 'single_ayt'
                    ? 'border-fuchsia-600 bg-fuchsia-50 text-fuchsia-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Sadece AYT
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{answerKeyLabel} <span className="text-red-500">*</span></label>
            <div className="flex flex-col gap-1.5">
              {isCreatingInternalYKS ? (
                <>
                  <div className={`grid grid-cols-1 gap-2 sm:grid-cols-2 ${newExamYksMode === 'single_tyt' ? 'items-stretch' : ''}`}>
                    {newExamYksMode !== 'single_ayt' && (
                    <label className="relative cursor-pointer">
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => setAnswerKeyTYTFile(e.target.files?.[0] || null)}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      />
                      <div className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-indigo-600 bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700">
                        <Upload size={16} />
                        <span className="truncate">{answerKeyTYTFile ? answerKeyTYTFile.name : 'TYT Cevap Anahtarı Seç'}</span>
                      </div>
                    </label>
                    )}
                    {(newExamYksMode === 'full' || newExamYksMode === 'single_ayt') && (
                      <label className="relative cursor-pointer">
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={(e) => setAnswerKeyAYTFile(e.target.files?.[0] || null)}
                          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        />
                        <div className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-fuchsia-600 bg-fuchsia-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-fuchsia-700">
                          <Upload size={16} />
                          <span className="truncate">{answerKeyAYTFile ? answerKeyAYTFile.name : 'AYT Cevap Anahtarı Seç'}</span>
                        </div>
                      </label>
                    )}
                    {newExamYksMode !== 'single_ayt' && (
                    <a
                      href="/tyt-ornek.xlsx"
                      download
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 whitespace-nowrap">
                      <Download size={16} />
                      TYT Örnek İndir
                    </a>
                    )}
                    {(newExamYksMode === 'full' || newExamYksMode === 'single_ayt') && (
                      <a
                        href="/ayt-ornek.xlsx"
                        download
                        className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 whitespace-nowrap">
                        <Download size={16} />
                        AYT Örnek İndir
                      </a>
                    )}
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {canShowInternalSingleSample ? (
                    <a
                      href={answerKeySampleHref}
                      download
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 whitespace-nowrap">
                      <Download size={16} />
                      {answerKeySampleText}
                    </a>
                  ) : (
                    <div className="inline-flex w-full items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-400">
                      Önce düzey seçin
                    </div>
                  )}
                  <label className="relative cursor-pointer">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setAnswerKeyFile(file);
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-blue-600 bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 whitespace-nowrap">
                      <Upload size={16} />
                      <span>{answerKeyFile ? answerKeyFile.name : "Dosya Seçin"}</span>
                    </div>
                  </label>
                </div>
              )}
              <p className="text-xs text-gray-600">{answerKeyHelpText}</p>
            </div>
          </div>

          {newExamSystemType === 'publisher' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Optik Form Tanımı (.fmt/.ftm/.bldm) <span className="text-red-500">*</span></label>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <label className="relative cursor-pointer">
                    <input
                      type="file"
                      // Uzantı süzgeci yok: macOS dosya penceresi .fmt gibi tanımadığı uzantıları soluk gösterip seçtirmiyor
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setFormDefinitionFile(file);
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="inline-flex items-center gap-2 px-3 py-2 border border-blue-600 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors whitespace-nowrap">
                      <Upload size={16} />
                      <span>{formDefinitionFile ? formDefinitionFile.name : "Dosya Seçin"}</span>
                    </div>
                  </label>
                  {formDefinitionFile && (
                    <button
                      onClick={() => setFormDefinitionFile(null)}
                      className="text-xs text-red-600 hover:underline">
                      Kaldır
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-600">
                  Yayıncı kuruluş optik okuyucu form tanım dosyasını yükleyin.
                </p>
              </div>
              <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={newExamAlanPuani}
                  onChange={(e) => setNewExamAlanPuani(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                <span>
                  <span className="font-medium">SAY / EA / SÖZ alan puanı ve alan sırası</span>
                  <span className="block text-xs text-gray-500">
                    11. sınıf alan denemeleri için: her test 40 soruya tamamlanır, alan 80 soru × 5 = 400 + 100 taban (en çok 500). Sıra aynı alandakiler arasındadır.
                  </span>
                </span>
              </label>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowNewExamModal(false);
                resetNewExamForm();
              }}
              disabled={creating}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              İptal
            </button>
            <button
              onClick={handleCreateExam}
              disabled={creating}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {creating ? "Oluşturuluyor..." : "Oluştur"}
            </button>
          </div>
        </div>
      </Modal>

      <OpticalUploadModal
        exam={opticalExam}
        isOpen={Boolean(opticalExam)}
        onClose={() => setOpticalExam(null)}
        onImported={() => setRefreshKey((k) => k + 1)}
      />

      <LgsOpticalUploadModal
        exam={lgsOpticalExam}
        isOpen={Boolean(lgsOpticalExam)}
        onClose={() => setLgsOpticalExam(null)}
        onImported={() => setRefreshKey((k) => k + 1)}
      />

      <Modal
        isOpen={showUploadResultsModal}
        onClose={() => {
          setShowUploadResultsModal(false);
          setSelectedExam(null);
          setResultsFile(null);
          setResultsFormFile(null);
        }}
        title={selectedExam ? `${selectedExam.name} - Sonuç Yükle` : "Sonuç Yükle"}
        size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sonuç Dosyası (TXT)</label>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <a
                  href="/sonuc.txt"
                  download
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors whitespace-nowrap">
                  <Download size={16} />
                  Örnek İndir
                </a>
                <label className="relative cursor-pointer">
                  <input
                    type="file"
                    accept=".txt"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setResultsFile(file);
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="inline-flex items-center gap-2 px-3 py-2 border border-blue-600 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors whitespace-nowrap">
                    <Upload size={16} />
                    <span>{resultsFile ? resultsFile.name : "Dosya Seçin"}</span>
                  </div>
                </label>
              </div>
              <p className="text-xs text-gray-600">Öğrenci sonuçlarının bulunduğu TXT dosyasını yükleyin.</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Optik Form Tanımı (.fmt / .ftm, önerilir)</label>
            <label className="relative inline-block cursor-pointer">
              <input
                type="file"
                onChange={(e) => setResultsFormFile(e.target.files?.[0] || null)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors whitespace-nowrap">
                <Upload size={16} />
                <span>{resultsFormFile ? resultsFormFile.name : "Form tanımı seçin"}</span>
              </div>
            </label>
            <p className="mt-1 text-xs text-gray-600">
              Optik okuyucunun form tanımını da verirseniz kitapçık, numara ve cevaplar alan alan doğru okunur. Test sayısı cevap anahtarıyla aynı olmalı.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowUploadResultsModal(false);
                setSelectedExam(null);
                setResultsFile(null);
              }}
              disabled={uploadingResults}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              İptal
            </button>
            <button
              onClick={handleUploadResults}
              disabled={!resultsFile || uploadingResults}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {uploadingResults ? "Yükleniyor..." : "Yükle"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showResultsModal}
        onClose={() => {
          setShowResultsModal(false);
          setResultsExam(null);
        }}
        title={resultsExam ? `${resultsExam.name} - Sonuçlar` : "Deneme Sonuçları"}
        size="xl">
        {resultsExam && (
          <ExamResultsTable
            exam={resultsExam}
            canViewSensitiveInfo={canViewSensitiveInfo}
            canEditIdentity={canUploadResults}
            onMetadata={setExamMetadata}
            onOpenDetail={(row) => {
              setSelectedStudentResult(row);
              setShowDetailModal(true);
            }}
          />
        )}
      </Modal>

      <Modal
        isOpen={showAnswerKeyModal}
        onClose={() => {
          setShowAnswerKeyModal(false);
          setAnswerKeyExam(null);
          setAnswerKeySheet(null);
          setOriginalAnswerKeySheet(null);
          setAnswerKeySheetTabs([]);
          setActiveAnswerKeySegment(null);
          setNewAnswerKeyUpload(null);
          handleCloseGainCodeEditor();
        }}
        title={answerKeyExam ? `${answerKeyExam.name} - Cevap Anahtarı` : 'Cevap Anahtarı'}
        size="xl">
        <div className="space-y-4">
          {answerKeyLoading ? (
            <p className="text-sm text-gray-500">Cevap anahtarı yükleniyor...</p>
          ) : (
            <>
              {answerKeySheetTabs.length > 1 && (
                <div className="flex justify-center">
                  <div className="grid w-full max-w-md grid-cols-2 rounded-2xl border border-slate-200 bg-slate-100/90 p-1.5 shadow-sm">
                    {answerKeySheetTabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => handleSwitchAnswerKeySegment(tab.key)}
                        className={`rounded-xl px-6 py-3 text-sm font-semibold tracking-[0.02em] transition-all ${activeAnswerKeySegment === tab.key
                          ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-100'
                          : 'text-slate-500 hover:bg-white/70 hover:text-slate-700'
                          }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className={`flex flex-wrap items-center gap-2 ${answerKeySheetTabs.length > 1 ? 'justify-center' : ''}`}>
                <button
                  onClick={handleDownloadAnswerKey}
                  disabled={answerKeyDownloadLoading}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50">
                  <Download size={16} />
                  {answerKeyDownloadLoading ? 'İndiriliyor...' : `${answerKeySheetTabs.length > 1 ? `${(answerKeySheetTabs.find((tab) => tab.key === activeAnswerKeySegment)?.label || 'Aktif')} ` : ''}Mevcut Dosyayı İndir`}
                </button>

                <label className="relative cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setNewAnswerKeyUpload(e.target.files?.[0] || null)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="inline-flex items-center gap-2 px-3 py-2 border border-amber-600 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition-colors whitespace-nowrap">
                    <Upload size={16} />
                    <span>{newAnswerKeyUpload ? newAnswerKeyUpload.name : `${answerKeySheetTabs.length > 1 ? `${(answerKeySheetTabs.find((tab) => tab.key === activeAnswerKeySegment)?.label || 'Aktif')} ` : ''}Yeni Cevap Anahtarı Seç`}</span>
                  </div>
                </label>

                <button
                  onClick={handleUpdateAnswerKey}
                  disabled={!newAnswerKeyUpload || answerKeyUpdating}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  <PenSquare size={16} />
                  {answerKeyUpdating ? 'Dosya İşleniyor...' : 'Dosyadan Güncelle'}
                </button>
                {answerKeySheet && hasAnswerKeySheetChanges && (
                  <button
                    onClick={handleSaveAnswerKeySheet}
                    disabled={answerKeyUpdating}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <CheckCircle size={16} />
                    {answerKeyUpdating ? 'Kaydediliyor...' : 'Düzenlemeleri Kaydet'}
                  </button>
                )}
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Yeni cevap anahtarı yüklendiğinde mevcut sonuçlar silinmez; öğrencilerin işaretleri korunarak yeni anahtarla yeniden puanlanır.
              </div>

              {!answerKeySheet ? (
                <p className="text-sm text-gray-500">Cevap anahtarında gösterilecek kayıt bulunamadı.</p>
              ) : (
                <div className="space-y-3">
                  <div ref={answerKeyGridRef} className="max-h-[65vh] overflow-auto border border-gray-200 rounded-xl bg-white">
                    <table className="min-w-full border-separate border-spacing-0 text-xs">
                      <thead className="sticky top-0 z-20 bg-slate-100">
                        <tr>
                          <th className="sticky left-0 z-30 w-12 border-b border-r border-slate-200 bg-slate-100 px-2 py-2 text-center font-semibold text-slate-500">
                            #
                          </th>
                          {answerKeySheet.columns.map((_, columnIndex) => (
                            <th
                              key={`col-letter-${columnIndex}`}
                              style={{ width: `${answerKeyColumnWidths[columnIndex] || 140}px`, minWidth: `${answerKeyColumnWidths[columnIndex] || 140}px` }}
                              className="border-b border-r border-slate-200 bg-slate-100 px-3 py-2 text-center font-semibold text-slate-600"
                            >
                              {getExcelColumnLabel(columnIndex)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="sticky left-0 z-10 border-b border-r border-slate-200 bg-slate-50 px-2 py-2 text-center font-semibold text-slate-500">1</td>
                          {answerKeySheet.columns.map((_, columnIndex) => (
                            <td key={`meta-1-${columnIndex}`} className="border-b border-r border-slate-200 bg-white px-0 py-0">
                              <input
                                value={columnIndex === 0 ? (answerKeySheet.publisherName || '') : columnIndex === 1 ? (answerKeySheet.testName || '') : ''}
                                onChange={(e) => {
                                  if (columnIndex === 0) handleAnswerKeyMetaChange('publisherName', e.target.value);
                                  if (columnIndex === 1) handleAnswerKeyMetaChange('testName', e.target.value);
                                }}
                                style={{ width: `${answerKeyColumnWidths[columnIndex] || 140}px`, minWidth: `${answerKeyColumnWidths[columnIndex] || 140}px` }}
                                className={`w-full bg-transparent px-3 py-2 text-slate-800 outline-none ${columnIndex === 0 || columnIndex === 1 ? 'text-center' : ''}`}
                              />
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="sticky left-0 z-10 border-b border-r border-slate-200 bg-slate-50 px-2 py-2 text-center font-semibold text-slate-500">2</td>
                          {answerKeySheet.columns.map((_, columnIndex) => (
                            <td key={`meta-2-${columnIndex}`} className="border-b border-r border-slate-200 bg-white px-0 py-0">
                              <input
                                value={columnIndex === 0 ? (answerKeySheet.testCode || '') : ''}
                                onChange={(e) => {
                                  if (columnIndex === 0) handleAnswerKeyMetaChange('testCode', e.target.value);
                                }}
                                style={{ width: `${answerKeyColumnWidths[columnIndex] || 140}px`, minWidth: `${answerKeyColumnWidths[columnIndex] || 140}px` }}
                                className={`w-full bg-transparent px-3 py-2 text-slate-800 outline-none ${columnIndex === 0 ? 'text-center' : ''}`}
                              />
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="sticky left-0 z-10 border-b border-r border-slate-200 bg-slate-50 px-2 py-2 text-center font-semibold text-slate-500">3</td>
                          {answerKeySheet.columns.map((column, columnIndex) => (
                            <td key={`header-${columnIndex}`} className="border-b border-r border-slate-200 bg-indigo-50 px-0 py-0">
                              <input
                                value={column || ''}
                                readOnly
                                style={{ width: `${answerKeyColumnWidths[columnIndex] || 140}px`, minWidth: `${answerKeyColumnWidths[columnIndex] || 140}px` }}
                                className="w-full cursor-default bg-transparent px-3 py-2 text-center font-semibold text-slate-800 outline-none"
                              />
                            </td>
                          ))}
                        </tr>
                        {answerKeySheet.rows.map((row, rowIndex) => (
                          <tr key={`sheet-row-${rowIndex}`} className="hover:bg-slate-50">
                            <td className="sticky left-0 z-10 border-b border-r border-slate-200 bg-slate-50 px-2 py-2 text-center font-semibold text-slate-500">
                              <div className="flex items-center justify-center gap-1">
                                <span>{rowIndex + 4}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveAnswerKeyRow(rowIndex)}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                  title="Satırı sil"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                            {answerKeySheet.columns.map((_, columnIndex) => (
                              <td key={`cell-${rowIndex}-${columnIndex}`} className="border-b border-r border-slate-200 bg-white px-0 py-0 align-top">
                                {columnIndex === 6 ? (
                                  <div
                                    style={{ width: `${answerKeyColumnWidths[columnIndex] || 160}px`, minWidth: `${answerKeyColumnWidths[columnIndex] || 160}px` }}
                                    className="flex flex-wrap items-center gap-2 px-3 py-2"
                                  >
                                    {(row?.[columnIndex] || '')
                                      .split(',')
                                      .map((item) => item.trim())
                                      .filter(Boolean)
                                      .map((gainCode) => (
                                        <button
                                          key={`${rowIndex}-${gainCode}`}
                                          type="button"
                                          onClick={() => handleRemoveGainCode(rowIndex, gainCode)}
                                          className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-200"
                                          title="Kaldır"
                                        >
                                          {gainCode}
                                        </button>
                                      ))}
                                    <button
                                      type="button"
                                      onClick={() => handleAddGainCode(rowIndex)}
                                      className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-slate-300 text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-700"
                                      title="Kazanım kodu ekle"
                                    >
                                      <Plus size={12} />
                                    </button>
                                  </div>
                                ) : (
                                  columnIndex === 1 && answerKeyTestDefinitions.length > 0 ? (
                                    <div
                                      style={{ width: `${answerKeyColumnWidths[columnIndex] || 140}px`, minWidth: `${answerKeyColumnWidths[columnIndex] || 140}px` }}
                                      className="relative"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => handleToggleAnswerKeyDropdown(rowIndex, 1)}
                                        className="flex h-full w-full items-center justify-center gap-2 bg-transparent px-3 py-2 text-center text-slate-800 outline-none transition-colors hover:bg-slate-50"
                                      >
                                        <span className="truncate">{row?.[columnIndex] || 'Seçin'}</span>
                                        <ChevronDown size={15} className={`shrink-0 text-slate-400 transition-transform ${answerKeyDropdown?.rowIndex === rowIndex && answerKeyDropdown?.columnIndex === 1 ? 'rotate-180' : ''}`} />
                                      </button>
                                      {answerKeyDropdown?.rowIndex === rowIndex && answerKeyDropdown?.columnIndex === 1 && (
                                        <div
                                          ref={answerKeyDropdownRef}
                                          className="absolute left-1/2 top-full z-30 mt-1 max-h-72 w-[calc(100%-8px)] min-w-[220px] -translate-x-1/2 overflow-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg"
                                        >
                                          <button
                                            type="button"
                                            onClick={() => handleSelectAnswerKeyDropdownOption(rowIndex, 1, '')}
                                            className={`flex w-full items-center justify-center rounded-lg px-3 py-2 text-center text-sm transition-colors ${!(row?.[columnIndex] || '').trim() ? 'bg-indigo-50 font-semibold text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
                                          >
                                            Seçin
                                          </button>
                                          {getAnswerKeyUniqueOptions(
                                            answerKeyTestDefinitions.map((item) => item.value),
                                            row?.[columnIndex] || ''
                                          ).map((option) => (
                                            <button
                                              key={option}
                                              type="button"
                                              onClick={() => handleSelectAnswerKeyDropdownOption(rowIndex, 1, option)}
                                              className={`flex w-full items-center justify-center rounded-lg px-3 py-2 text-center text-sm transition-colors ${(row?.[columnIndex] || '') === option ? 'bg-indigo-600 font-semibold text-white shadow-sm' : 'text-slate-700 hover:bg-slate-50'}`}
                                            >
                                              {option}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ) : columnIndex === 2 && answerKeyTestDefinitions.length > 0 ? (
                                    <div
                                      style={{ width: `${answerKeyColumnWidths[columnIndex] || 140}px`, minWidth: `${answerKeyColumnWidths[columnIndex] || 140}px` }}
                                      className="relative"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => handleToggleAnswerKeyDropdown(rowIndex, 2)}
                                        className="flex h-full w-full items-center justify-center gap-2 bg-transparent px-3 py-2 text-center text-slate-800 outline-none transition-colors hover:bg-slate-50"
                                      >
                                        <span className="truncate">{row?.[columnIndex] || 'Seçin'}</span>
                                        <ChevronDown size={15} className={`shrink-0 text-slate-400 transition-transform ${answerKeyDropdown?.rowIndex === rowIndex && answerKeyDropdown?.columnIndex === 2 ? 'rotate-180' : ''}`} />
                                      </button>
                                      {answerKeyDropdown?.rowIndex === rowIndex && answerKeyDropdown?.columnIndex === 2 && (
                                        <div
                                          ref={answerKeyDropdownRef}
                                          className="absolute left-1/2 top-full z-30 mt-1 max-h-72 w-[calc(100%-8px)] min-w-[220px] -translate-x-1/2 overflow-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg"
                                        >
                                          <button
                                            type="button"
                                            onClick={() => handleSelectAnswerKeyDropdownOption(rowIndex, 2, '')}
                                            className={`flex w-full items-center justify-center rounded-lg px-3 py-2 text-center text-sm transition-colors ${!(row?.[columnIndex] || '').trim() ? 'bg-indigo-50 font-semibold text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
                                          >
                                            Seçin
                                          </button>
                                          {getAnswerKeyUniqueOptions(
                                            getAnswerKeySubjectsForTest(answerKeyExam?.levelType, row?.[1] || ''),
                                            row?.[columnIndex] || ''
                                          ).map((option) => (
                                            <button
                                              key={option}
                                              type="button"
                                              onClick={() => handleSelectAnswerKeyDropdownOption(rowIndex, 2, option)}
                                              className={`flex w-full items-center justify-center rounded-lg px-3 py-2 text-center text-sm transition-colors ${(row?.[columnIndex] || '') === option ? 'bg-indigo-600 font-semibold text-white shadow-sm' : 'text-slate-700 hover:bg-slate-50'}`}
                                            >
                                              {option}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <input
                                      value={row?.[columnIndex] || ''}
                                      onChange={(e) => handleAnswerKeyCellChange(rowIndex, columnIndex, e.target.value)}
                                      style={{ width: `${answerKeyColumnWidths[columnIndex] || 140}px`, minWidth: `${answerKeyColumnWidths[columnIndex] || 140}px` }}
                                      className={`w-full bg-transparent px-3 py-2 text-slate-800 outline-none ${isCenteredAnswerKeyColumn(columnIndex) ? 'text-center' : ''}`}
                                    />
                                  )
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr className="bg-emerald-50/60">
                          <td className="sticky left-0 z-10 border-b border-r border-slate-200 bg-emerald-50 px-2 py-2 text-center font-semibold text-emerald-700">
                            {answerKeySheet.rows.length + 4}
                          </td>
                          {answerKeySheet.columns.map((_, columnIndex) => (
                            columnIndex === 1 ? null : (
                              <td
                                key={`append-row-${columnIndex}`}
                                colSpan={columnIndex === 0 ? 2 : 1}
                                className="border-b border-r border-slate-200 bg-emerald-50 px-0 py-0 align-top"
                              >
                                {columnIndex === 0 ? (
                                  <button
                                    type="button"
                                    onClick={handleAddAnswerKeyRow}
                                    style={{
                                      width: `${(answerKeyColumnWidths[0] || 140) + (answerKeyColumnWidths[1] || 140)}px`,
                                      minWidth: `${(answerKeyColumnWidths[0] || 140) + (answerKeyColumnWidths[1] || 140)}px`
                                    }}
                                    className="inline-flex items-center justify-center gap-2 px-3 py-3 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
                                    title="Yeni satır ekle"
                                  >
                                    <Plus size={16} />
                                    <span>Yeni Satır Ekle</span>
                                  </button>
                                ) : (
                                  <div
                                    style={{ width: `${answerKeyColumnWidths[columnIndex] || 140}px`, minWidth: `${answerKeyColumnWidths[columnIndex] || 140}px` }}
                                    className="h-full min-h-[48px]"
                                  />
                                )}
                              </td>
                            )
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-xs text-slate-500">
                      Düzenlemeleri kaydetmeden bu ekrandan çıkarsanız yaptığınız değişiklikler saklanmaz.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={gainCodeEditorRowIndex !== null}
        onClose={handleCloseGainCodeEditor}
        title="Kazanım Kodu Ekle"
        size="sm">
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Yeni Kazanım Kodu
            </label>
            <input
              value={pendingGainCode}
              onChange={(e) => setPendingGainCode(e.target.value)}
              placeholder="Örn. 21.1.2"
              className="w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition-colors focus:border-indigo-400"
              autoFocus
            />
            {gainCodeEditorRowIndex !== null && (
              <p className="mt-1 text-xs text-slate-500">
                Satır {gainCodeEditorRowIndex + 4} için yeni kazanım kodu ekleyin.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button
              type="button"
              onClick={handleCloseGainCodeEditor}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleConfirmAddGainCode}
              disabled={!pendingGainCode.trim()}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={14} />
              Ekle
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setExamToDelete(null);
        }}
        title="Deneme Sil"
        size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            <strong>{examToDelete?.name}</strong> adlı denemeyi silmek istediğinize emin misiniz?
            <br /><br />
            <span className="text-red-600 font-medium">Bu işlem geri alınamaz. Denemeye ait tüm sonuçlar, cevap anahtarı ve raporlar kalıcı olarak silinecektir.</span>
          </p>

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setExamToDelete(null);
              }}
              disabled={deleting}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              İptal
            </button>
            <button
              onClick={handleDeleteExam}
              disabled={deleting}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {deleting ? "Siliniyor..." : "Sil"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedStudentResult(null);
        }}
        title={`${selectedStudentResult?.fullName || 'Öğrenci'} - Detaylı Sonuçlar`}
        size="xl">
        {selectedStudentResult && (() => {
          const testResults = selectedStudentResult?.results?.testResults || {};
          // Alan puanlı yayınevi denemesinde başarı yalnız alanın testlerinden (11. sınıf: 120 sorunun alandaki 60'ı)
          const alanTotals = selectedStudentResult?.results?.alanPuani?.totals;
          const genelBasari = alanTotals?.questions ? (alanTotals.correct / alanTotals.questions) * 100 : (selectedStudentResult?.stats?.scorePercentage || 0);
          const bookletGroups: { [key: number]: { bookletNo: number; bookletName: string; tests: any[]; totalCorrect: number; totalIncorrect: number; totalUnanswered: number; totalQuestions: number } } = {};

          Object.entries(testResults).forEach(([testNum, testData]: [string, any]) => {
            const bookletInfo = examMetadata?.bookletMapping?.[testNum];
            const bookletNo = bookletInfo?.bookletNo || 1;
            const bookletName = bookletNo === 1 ? 'Oturum 1' : bookletNo === 2 ? 'Oturum 2' : `Oturum ${bookletNo}`;

            if (!bookletGroups[bookletNo]) {
              bookletGroups[bookletNo] = {
                bookletNo,
                bookletName,
                tests: [],
                totalCorrect: 0,
                totalIncorrect: 0,
                totalUnanswered: 0,
                totalQuestions: 0
              };
            }

            bookletGroups[bookletNo].tests.push({ ...testData, testNum });
            bookletGroups[bookletNo].totalCorrect += testData.correct || 0;
            bookletGroups[bookletNo].totalIncorrect += testData.incorrect || 0;
            bookletGroups[bookletNo].totalUnanswered += testData.unanswered || 0;
            bookletGroups[bookletNo].totalQuestions += testData.totalQuestions || 0;
          });

          return (
            <div className="space-y-6">

              <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200/50 shadow-lg">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                      <PieChart className="w-5 h-5 text-white" />
                    </div>
                    Genel Özet
                  </h3>
                  <div className={`px-4 py-2 rounded-full text-sm font-bold shadow-sm ${genelBasari >= 70
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white'
                    : genelBasari >= 50
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                      : 'bg-gradient-to-r from-red-500 to-rose-600 text-white'
                    }`}>
                    {genelBasari >= 70 ? 'Başarılı' : genelBasari >= 50 ? 'Orta' : 'Geliştirilmeli'}
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">Genel Başarı Oranı</span>
                    <span className={`text-lg font-bold ${genelBasari >= 50 ? 'text-emerald-600' : 'text-red-600'}`}>
                      %{Number(genelBasari).toFixed(1)}
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${genelBasari >= 70
                        ? 'bg-gradient-to-r from-emerald-400 to-green-500'
                        : genelBasari >= 50
                          ? 'bg-gradient-to-r from-amber-400 to-orange-500'
                          : 'bg-gradient-to-r from-red-400 to-rose-500'
                        }`}
                      style={{ width: `${Math.min(genelBasari, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Toplam Soru</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-800">
                      {selectedStudentResult?.stats?.totalQuestions || 0}
                    </div>
                  </div>

                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-emerald-200/50 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Doğru</span>
                    </div>
                    <div className="text-3xl font-bold text-emerald-600">
                      {selectedStudentResult?.stats?.correctAnswers || 0}
                    </div>
                  </div>

                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-red-200/50 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Yanlış</span>
                    </div>
                    <div className="text-3xl font-bold text-red-600">
                      {selectedStudentResult?.stats?.incorrectAnswers || 0}
                    </div>
                  </div>

                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-gray-500" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Boş</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-500">
                      {selectedStudentResult?.stats?.unansweredQuestions || 0}
                    </div>
                  </div>
                </div>
              </div>

              {Object.keys(bookletGroups).length > 0 && (
                <div className="space-y-4">
                  {Object.values(bookletGroups).sort((a, b) => a.bookletNo - b.bookletNo).map((booklet) => {
                    const bookletPercentage = booklet.totalQuestions > 0
                      ? (booklet.totalCorrect / booklet.totalQuestions * 100)
                      : 0;

                    return (
                      <div key={booklet.bookletNo} className="border border-indigo-200 rounded-xl overflow-hidden">

                        <div className="bg-gradient-to-r from-indigo-100 to-purple-100 p-4 border-b border-indigo-200">
                          <div className="flex items-center justify-between">
                            <h4 className="text-lg font-bold text-indigo-800 flex items-center gap-2">
                              <Layers className="w-5 h-5 text-indigo-600" />
                              {booklet.bookletName}
                            </h4>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-indigo-600">{booklet.totalQuestions} Soru</span>
                              <span className="text-emerald-600 font-medium">{booklet.totalCorrect} Doğru</span>
                              <span className="text-red-600 font-medium">{booklet.totalIncorrect} Yanlış</span>
                              <span className="text-gray-500 font-medium">{booklet.totalUnanswered} Boş</span>
                              <span className={`font-bold text-lg ${bookletPercentage >= 50 ? 'text-emerald-600' : 'text-red-600'}`}>
                                %{bookletPercentage.toFixed(1)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 space-y-3 bg-white">
                          {booklet.tests.map((testData: any) => (
                            <div key={testData.testNum} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="font-semibold text-gray-700 flex items-center gap-2">
                                  <BookOpen className="w-4 h-4 text-gray-500" />
                                  Test {testData.testNum}: {testData.testName || 'Test'}
                                </h5>
                                <div className="flex items-center gap-3 text-sm">
                                  <span className="text-gray-500">{testData.totalQuestions} Soru</span>
                                  <span className={`font-bold ${(testData.percentage || 0) >= 50 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    %{Number(testData.percentage || 0).toFixed(1)}
                                  </span>
                                </div>
                              </div>

                              {testData.subjects && testData.subjects.length > 0 && (
                                <div className="space-y-1.5 mt-2">
                                  {testData.subjects.map((subject: any, idx: number) => (
                                    <div key={idx} className="bg-white rounded-md p-2 flex items-center justify-between text-sm border border-gray-100">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-700">{subject.name}</span>
                                        <span className="text-xs text-gray-400">({subject.totalQuestions} Soru)</span>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <span className="text-emerald-600">{subject.correct} <span className="text-gray-400 text-xs">Doğru</span></span>
                                        <span className="text-red-600">{subject.incorrect} <span className="text-gray-400 text-xs">Yanlış</span></span>
                                        <span className="text-gray-500">{subject.unanswered} <span className="text-gray-400 text-xs">Boş</span></span>
                                        <span className={`font-semibold min-w-[50px] text-right ${subject.percentage >= 50 ? 'text-emerald-600' : 'text-red-600'}`}>
                                          %{Number(subject.percentage).toFixed(0)}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {testData.questions && testData.questions.length > 0 && (
                                <div className="mt-4">
                                  <h6 className="text-sm font-semibold text-red-600 mb-2">Hatalı Sorular ve Kazanımlar</h6>
                                  <div className="space-y-2">
                                    {testData.questions
                                      .filter((q: any) => !q.isCorrect)
                                      .map((q: any, qIdx: number) => (
                                        <div key={qIdx} className="bg-red-50 rounded-md p-3 border border-red-100 text-sm">
                                          <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-bold text-xs">
                                              {q.questionNumber}
                                            </div>
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2 mb-1">
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${!q.isAnswered ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-600'}`}>
                                                  {!q.isAnswered ? 'BOŞ' : 'YANLIŞ'}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                  Cevabınız: <span className="font-medium">{q.studentAnswer || '-'}</span>
                                                </span>
                                                <span className="text-xs text-gray-500 border-l pl-2 border-gray-300">
                                                  Doğru: <span className="font-bold text-emerald-600">{q.correctAnswer}</span>
                                                </span>
                                              </div>
                                              {q.achievement ? (
                                                <p className="text-gray-700 text-xs italic">
                                                  <span className="font-semibold">Kazanım:</span> {q.achievement}
                                                </p>
                                              ) : (
                                                <p className="text-gray-400 text-xs italic">Kazanım bilgisi yok</p>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    {testData.questions.filter((q: any) => !q.isCorrect).length === 0 && (
                                      <p className="text-sm text-emerald-600 italic">Harika! Bu testte hiç yanlış veya boş yok.</p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
    </>
  );
}